import { useEffect, useState, useCallback, useMemo } from 'react';
import { useParams } from 'react-router-dom';
import { publicDocumentApi, type DirectInvoiceResponse, type PublicBusinessContactInfo } from '@/lib/api';
import { calculateSubtotal, transformLineItems } from '@/components/TemplateDesigner/utils';
import { DocumentRenderer, buildOwlOpsPayload } from '@/components/TemplateDesigner/DocumentRenderer';
import type { TemplateSettings, DocumentData, BusinessData, DocumentSignature, SignatureLine, DocumentContract } from '@/components/TemplateDesigner/types';
import { DocumentNotFound } from './DocumentNotFound';
import { CheckoutLayout } from './CheckoutLayout';
import { FullDocumentView } from './FullDocumentView';
import { ClientSignatureModal } from '@/components/Modals/ClientSignatureModal';
import { Loader2 } from 'lucide-react';

export function PublicInvoice() {
  const { id } = useParams<{ id: string }>();
  const [data, setData] = useState<DirectInvoiceResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [notFoundBusiness, setNotFoundBusiness] = useState<PublicBusinessContactInfo | null>(null);
  const [signingModalOpen, setSigningModalOpen] = useState(false);
  const [signing, setSigning] = useState(false);

  // Prevent search engine indexing
  useEffect(() => {
    const meta = document.createElement('meta');
    meta.name = 'robots';
    meta.content = 'noindex, nofollow';
    document.head.appendChild(meta);
    return () => { document.head.removeChild(meta); };
  }, []);

  useEffect(() => {
    if (!id) return;

    const loadInvoice = async () => {
      const response = await publicDocumentApi.getInvoice(id);

      if (response.error) {
        // Try to extract business info from error response for not found page
        try {
          const errorData = JSON.parse(response.error);
          if (errorData.business) {
            setNotFoundBusiness(errorData.business);
          }
        } catch {
          // Ignore parse errors
        }
        setLoading(false);
        return;
      }

      if (response.data) {
        setData(response.data);
      }
      setLoading(false);
    };

    loadInvoice();
  }, [id]);

  // Extract data from response (or use defaults for hooks)
  const invoice = data?.invoice;
  const client = data?.client;
  const business = data?.business;
  const taxes = data?.taxes;
  const template_config = data?.template_config;
  const client_reviews_enabled = data?.client_reviews_enabled ?? false;

  // Get tax total for checkout display (use backend calculation)
  const totalTax = useMemo(() => {
    if (!invoice) return 0;
    return parseFloat(invoice.total_tax);
  }, [invoice]);

  // Get template settings from template_config
  const templateSettings = template_config?.settings as unknown as TemplateSettings | undefined;

  // Build document data for renderer (memoized, handles null data)
  const documentData: DocumentData | null = useMemo(() => {
    if (!invoice || !client || !taxes) return null;

    // Build signatures and signature_lines from contract_instance
    const signatures: DocumentSignature[] = [];
    const signature_lines: SignatureLine[] = [];
    let contract: DocumentContract | undefined;

    const ci = invoice.contract_instance;
    if (ci) {
      // Build contract object
      contract = {
        content: ci.cached_rendered_content,
        requires_business_signature: ci.business_signature_required,
        requires_client_signature: ci.counterparty_signature_required,
      };

      // Add signature lines for required signatures (defines where signatures appear)
      if (ci.business_signature_required) {
        signature_lines.push({
          role: 'business',
          label: 'Authorized Signature',
        });
      }
      if (ci.counterparty_signature_required) {
        signature_lines.push({
          role: 'client',
          label: 'Client Signature',
        });
      }

      // Add captured signatures (fills in the data for signed lines)
      if (ci.business_signed_at && ci.business_signatory) {
        signatures.push({
          role: 'business',
          label: 'Authorized Signature',
          name: ci.business_signatory,
          image: ci.business_signature_image || undefined,
          signed_at: ci.business_signed_at,
        });
      }
      if (ci.counterparty_signed_at && ci.counterparty_signatory) {
        signatures.push({
          role: 'client',
          label: 'Client Signature',
          name: ci.counterparty_signatory,
          image: ci.counterparty_signature_image || undefined,
          signed_at: ci.counterparty_signed_at,
        });
      }
    }

    // Use computed values (markup baked into line items)
    const computedSubtotal = calculateSubtotal(invoice.line_items);

    return {
      documentType: 'invoice' as const,
      id: invoice.id,
      invoice_number: invoice.invoice_number,
      invoice_date: invoice.invoice_date || '',
      due_date: invoice.due_date || '',
      payment_terms: invoice.payment_terms,
      status: invoice.status,
      client_name: client.billing_name,
      client_company: client.company_name || '',
      client_address: client.address || '',
      note_for_client: invoice.note_for_client || '',
      line_items: transformLineItems(invoice.line_items, taxes),
      subtotal: computedSubtotal,
      total_tax: parseFloat(invoice.total_tax),  // Use backend calculation (accounts for discount)
      total_markup: 0,  // Markup is baked into line item rates, don't show separately
      total_discount: parseFloat(invoice.total_discount),
      total_amount: parseFloat(invoice.total_amount),
      amount_paid: parseFloat(invoice.amount_paid),
      balance_due: parseFloat(invoice.balance_due),
      // Custom fields
      custom_field_values: invoice.custom_field_values,
      // Shipping data
      ship_enabled: invoice.ship_enabled,
      ship_fee: invoice.ship_fee,
      ship_date: invoice.ship_date,
      ship_via: invoice.ship_via,
      ship_tracking_number: invoice.ship_tracking_number,
      // Contract and signature data
      ...(signatures.length > 0 ? { signatures } : {}),
      ...(signature_lines.length > 0 ? { signature_lines } : {}),
      ...(contract ? { contract } : {}),
    };
  }, [invoice, client, taxes, totalTax]);

  const businessData: BusinessData | null = useMemo(() => {
    if (!business) return null;
    return {
      name: business.name,
      address: business.address || '',
      phone: business.phone || '',
      email: business.email || '',
      logo: business.logo || '',
      payment_instructions: business.payment_instructions || '',
    };
  }, [business]);

  // On-demand PDF generation callback
  const handleDownloadPdf = useCallback(async () => {
    if (!templateSettings || !documentData || !businessData) {
      throw new Error('Template settings not available');
    }
    const payload = buildOwlOpsPayload(documentData, businessData, templateSettings);
    return publicDocumentApi.renderPdf({
      template_slug: templateSettings.selectedLayout,
      data: payload.data,
      settings: payload.settings,
    });
  }, [documentData, businessData, templateSettings]);

  const handleSignClient = useCallback(async (signatory_name: string, signature_image: string) => {
    if (!id) return;
    setSigning(true);

    const response = await publicDocumentApi.signInvoiceClient(id, signatory_name, signature_image);

    if (response.error) {
      setSigning(false);
      return;
    }

    // Close the modal
    setSigningModalOpen(false);
    setSigning(false);

    // Update local state to reflect signature collected
    // This will trigger re-render and show checkout if stripe is enabled
    if (data && response.data) {
      setData({
        ...data,
        invoice: {
          ...data.invoice,
          contract_instance: data.invoice.contract_instance
            ? {
                ...data.invoice.contract_instance,
                counterparty_signatory: response.data.invoice.contract_instance.counterparty_signatory,
                counterparty_signature_image: response.data.invoice.contract_instance.counterparty_signature_image,
                counterparty_signed_at: response.data.invoice.contract_instance.counterparty_signed_at,
              }
            : null,
        },
      });
    }
  }, [id, data]);

  // Early returns AFTER all hooks
  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-gray-400" />
      </div>
    );
  }

  if (!data || !invoice || !client || !business) {
    return <DocumentNotFound documentType="Invoice" business={notFoundBusiness} />;
  }

  // Signature requirement detection
  const contractInstance = invoice.contract_instance;
  const requiresClientSignature = !!(
    contractInstance?.counterparty_signature_required &&
    !contractInstance?.counterparty_signed_at
  );
  const signatureCollected = !!contractInstance?.counterparty_signed_at;
  const hasStripePayment = invoice.stripe_card_enabled || invoice.stripe_ach_enabled;

  // Show checkout layout when: Stripe payment enabled (signature handled inside checkout)
  const showCheckout =
    invoice.status === 'awaiting_payment' &&
    hasStripePayment;

  // For invoices without Stripe payment but with signature required - show FullDocumentView with sign button
  const showSignButton =
    invoice.status === 'awaiting_payment' &&
    requiresClientSignature &&
    !showCheckout;

  // Determine sign button text based on flow
  const getSignButtonText = () => {
    return 'Sign';
  };

  // Build document content using DocumentRenderer (same approach as Template Designer)
  const documentContent = templateSettings && documentData && businessData ? (
    <DocumentRenderer
      isPublic
      data={documentData}
      settings={templateSettings}
      business={businessData}
    />
  ) : (
    <div className="p-8 text-center text-gray-500">
      Unable to render document. Please try again later.
    </div>
  );

  // Checkout view for unpaid invoices with payment enabled
  if (showCheckout) {
    return (
      <CheckoutLayout
        documentType="invoice"
        documentId={invoice.id}
        clientId={client.id}
        business={business}
        stripeAccountId="" // Will be fetched from payment info endpoint
        documentNumber={invoice.invoice_number}
        documentDate={invoice.invoice_date}
        dueDate={invoice.due_date}
        subtotal={parseFloat(invoice.subtotal)}
        shippingFee={invoice.ship_enabled ? parseFloat(invoice.ship_fee || '0') : 0}
        totalTax={totalTax}
        totalAmount={parseFloat(invoice.total_amount)}
        amountPaid={parseFloat(invoice.amount_paid)}
        balanceDue={parseFloat(invoice.balance_due)}
        cardEnabled={invoice.stripe_card_enabled}
        achEnabled={invoice.stripe_ach_enabled}
        availableCredit={0} // Will be fetched from payment info endpoint
        clientName={client.billing_name}
        clientCompany={client.company_name}
        clientAddress={client.address}
        requiresSignature={contractInstance?.counterparty_signature_required ?? false}
        signatureCollected={signatureCollected}
        contractContent={contractInstance?.cached_rendered_content}
        onSignComplete={handleSignClient}
        onPaymentSuccess={() => window.location.reload()}
        fullDocumentContent={documentContent}
        pdfUrl={invoice.pdf_url}
        onDownloadPdf={templateSettings && documentData && businessData ? handleDownloadPdf : undefined}
        pdfFilename={`Invoice-${invoice.invoice_number}.pdf`}
        clientReviewsEnabled={client_reviews_enabled}
      />
    );
  }

  // Full document view for paid invoices or invoices without payment
  const statusConfig = getInvoiceStatusConfig(invoice.status);

  return (
    <>
      <FullDocumentView
        documentType="invoice"
        business={business}
        themeColor={templateSettings?.colorTheme?.primary}
        logoUrl={templateSettings?.logo?.url}
        showCompanyName={templateSettings?.logo?.showCompanyName ?? true}
        status={invoice.status}
        statusLabel={statusConfig.label}
        statusVariant={invoice.status === 'paid' ? 'success' : 'default'}
        showSignButton={showSignButton}
        signButtonText={getSignButtonText()}
        onSign={() => setSigningModalOpen(true)}
        signing={signing}
        pdfUrl={invoice.pdf_url}
        onDownloadPdf={templateSettings && documentData && businessData ? handleDownloadPdf : undefined}
        pdfFilename={`Invoice-${invoice.invoice_number}.pdf`}
      >
        {documentContent}
      </FullDocumentView>

      {/* Client Signature Modal */}
      {contractInstance && (
        <ClientSignatureModal
          open={signingModalOpen}
          onClose={() => setSigningModalOpen(false)}
          onComplete={handleSignClient}
          contractContent={contractInstance.cached_rendered_content}
          businessName={business.name}
          defaultName={client.billing_name}
        />
      )}
    </>
  );
}

// Status config helper (simplified from lib/invoiceStatus)
function getInvoiceStatusConfig(status: string) {
  switch (status) {
    case 'paid':
      return { label: 'Paid' };
    case 'awaiting_payment':
      return { label: 'Awaiting Payment' };
    default:
      return { label: status };
  }
}
